import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  message: string;
  tone?: ToastTone;
  durationMs?: number;
}

interface ToastContextValue {
  show: (msg: string, opts?: { tone?: ToastTone; durationMs?: number }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_CLASSES: Record<ToastTone, string> = {
  info: 'bg-blue text-white',
  success: 'bg-green text-white',
  warning: 'bg-accent text-white',
  error: 'bg-red text-white',
};

interface ToastViewportProps {
  children: ReactNode;
}

export function ToastViewport({ children }: ToastViewportProps) {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const show = useCallback(
    (message: string, opts?: { tone?: ToastTone; durationMs?: number }) => {
      const id = crypto.randomUUID();
      const tone = opts?.tone ?? 'info';
      const durationMs = opts?.durationMs ?? 3500;
      setToasts((prev) => [...prev, { id, message, tone, durationMs }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, durationMs);
    },
    [],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((t) => (
          <Toast key={t.id} {...t} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function Toast({ message, tone = 'info' }: ToastProps) {
  return (
    <div
      className={cn(
        'pointer-events-auto rounded-card-lg px-4 py-3 shadow-card-hover text-sm font-medium',
        'max-w-mobile w-full',
        TONE_CLASSES[tone],
      )}
    >
      {message}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be called inside <ToastViewport>');
  }
  return ctx;
}

// Re-export the timer hook nothing-but for tree-shaking parity
export { useEffect };
